import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { CheckCircle2, MapPin, User, Calendar, ArrowRight } from "lucide-react";

const PROCESS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8f287472-b53b-4254-a417-af9039f0ca84/images/2b0052fb5b27b521417b827da451fd2574b06544fdc4eec760e38da4fb43fb5b.png";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About"
        breadcrumb="About"
        title="An independent electronics reseller built on quality and transparency."
        subtitle="CNL Tech is a small independently owned business based in Corona, California, founded by Marcelo Peñaloza and serving customers since 2020."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-5 text-[15.5px] leading-relaxed text-gray-700">
            <p>
              CNL Tech (operating as CNL Toys) was founded in 2020 with a
              simple goal: provide quality refurbished, open-box, customer-return,
              and overstock electronics at prices customers can feel good
              about, backed by transparent grading and real customer support.
            </p>
            <p>
              For more than 8 years, we’ve sourced inventory through customer
              returns, liquidation lots, and overstock purchases, then put
              each item through a consistent inspection and testing workflow
              before listing it for resale.
            </p>
            <p>
              We’re a small, independently owned business — and we like it that
              way. It means every product decision, every condition grade, and
              every customer reply comes from a team that actually cares about
              what we’re selling.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-gray-200 rounded-sm overflow-hidden">
              <img
                src={PROCESS_IMG}
                alt="Inventory warehouse with shelves of electronics"
                className="w-full h-[360px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F9FAFB] border-y border-gray-200">
        <div className="cnl-container py-16 lg:py-20 grid sm:grid-cols-3 gap-4">
          {[
            { icon: Calendar, t: "Established", v: "2020" },
            { icon: MapPin, t: "Headquarters", v: "Corona, California" },
            { icon: User, t: "Owner", v: "Marcelo Peñaloza" },
          ].map((s) => (
            <div
              key={s.t}
              className="border border-gray-200 rounded-sm bg-white p-7"
              data-testid={`about-stat-${s.t.toLowerCase()}`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#0B192C] text-white">
                <s.icon size={18} />
              </div>
              <div className="mt-5 text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">
                {s.t}
              </div>
              <div className="mt-1 text-[20px] font-bold text-[#0B192C]">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24 max-w-3xl">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
            What we stand for
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
            Hardware only. Honest grading. Helpful support.
          </h2>
          <ul className="mt-8 space-y-4">
            {[
              "We sell hardware products only — no subscriptions, no services.",
              "Every product is categorized using a clear condition grade.",
              "Customer questions get a real reply during business hours.",
              "Refurbishment helps reduce e-waste and extend hardware life.",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px] text-gray-700">
                <CheckCircle2 size={20} className="text-[#16A34A] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contact"
            className="mt-10 inline-flex items-center gap-2 rounded-sm bg-[#0B192C] px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E3E62]"
            data-testid="about-contact-cta"
          >
            Talk to us <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
